import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { editorMessages } from '../editorMessages';
import { EditorStatus, editorStatusLabel } from './editorStatus';
import { EditorPublish } from './useEditorPublish';
import { SMLEditorPublishButton } from './SMLEditorPublishButton';
import { SMLEditorPublishAlert } from './SMLEditorPublishAlert';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLEditorTopBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLEditorTopBarProps {
  accountId: number;
  name: string;
  isBioLink: boolean;
  /** The public address without the scheme: "laziibee.music24.link/swamps". */
  address: string;
  status: EditorStatus;
  blockCount: number;
  publish: EditorPublish;
  onBack: () => void;
  onOpenPageTab: () => void;
  onOpenPage: () => void;
  /** Below 992 px the bar is one compact line; the bottom bar holds Preview and Publish (design a8). */
  isCompact: boolean;
}

/** The top bar of the editor (design a1–a4): back, the page, its address, the status and the actions. */
const SMLEditorTopBarComponent: React.FC<SMLEditorTopBarProps> = ({
  accountId,
  name,
  isBioLink,
  address,
  status,
  blockCount,
  publish,
  onBack,
  onOpenPageTab,
  onOpenPage,
  isCompact,
}) => {
  const intl = useIntl();
  const isDraft = status.kind === 'draft';

  return (
    <div className={styles.wrap}>
      <div className={styles.bar}>
        <button
          type="button"
          className={styles.back}
          onClick={onBack}
          aria-label={intl.formatMessage(editorMessages.back)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className={styles.identity}>
          {!isCompact && <span className={styles.crumb}>{intl.formatMessage(editorMessages.crumb)}</span>}
          <span className={styles.name}>
            <span className={styles.nameText}>{name}</span>
            {!isCompact && (
              <span className={styles.kind}>
                {intl.formatMessage(isBioLink ? editorMessages.kindBioLink : editorMessages.kindSong)}
              </span>
            )}
          </span>
        </div>
        <button
          type="button"
          className={clsx(styles.address, publish.state.kind === 'slugTaken' && styles.addressBad)}
          onClick={onOpenPageTab}
          title={intl.formatMessage(editorMessages.addressChip)}
          data-testid="editor-address-chip"
        >
          <span className={styles.addressText}>{address}</span>
          <FontAwesomeIcon icon={faPen} className={styles.addressIcon} />
        </button>
        <span className={styles.spacer} />
        <span
          className={clsx(
            styles.pill,
            status.kind === 'draft' && styles.pillDraft,
            status.kind === 'upToDate' && styles.pillLive,
            status.kind === 'pending' && styles.pillPending
          )}
          data-testid="editor-status"
        >
          <span className={styles.dot} />
          {editorStatusLabel(intl, status)}
        </span>
        {!isCompact && (
          <>
            <SMLButton
              outline
              leftIcon={faArrowUpRightFromSquare}
              onClick={onOpenPage}
              disabled={publish.state.kind === 'publishing'}
              omitCap
            >
              {intl.formatMessage(isDraft ? editorMessages.preview : editorMessages.openPage)}
            </SMLButton>
            <SMLEditorPublishButton accountId={accountId} status={status} blockCount={blockCount} publish={publish} />
          </>
        )}
      </div>
      <SMLEditorPublishAlert address={address} publish={publish} onOpenPageTab={onOpenPageTab} />
    </div>
  );
};

export const SMLEditorTopBar = TypedMemo(SMLEditorTopBarComponent);
